import {
  ActionList,
  ActionListItem,
  Alert,
  Box,
  Button,
  Dropdown,
  DropdownOverlay,
  Heading,
  SelectInput,
  Text,
  TextArea,
  TextInput,
} from '@razorpay/blade/components';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
} from 'components/WizardViews/components';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import { useFormWithProvider } from 'forms';
import React, { FormEvent, useMemo } from 'react';
import {
  PAYMENT_STATUS_OPTIONS,
  PAYOUT_MONTH_HELP_TEXT,
  bonusDetailsValidationSchema,
  bonusSteps,
  getTdsSettingLabel,
} from './constants';
import { BonusFormData } from './types';
import { useBonusTypes } from './hooks/useBonusTypes';
import { usePayrollMonthStatus } from './hooks/usePayrollMonthStatus';
import {
  findBonusType,
  formatBonusAmountInput,
  getPayoutMonthOptions,
  parseBonusAmountInput,
} from './utils';

const BonusDetailsStep: React.FC = () => {
  const { formData, setFieldValue, validate, register } = useFormWithProvider<BonusFormData>();
  const { updateStepStatus, setCurrentStep, closeWizard } = useWizardInfo();
  const { bonusTypes, isLoading, isError, refetch } = useBonusTypes();

  const isEdit = Boolean(formData.assignmentId);

  const selectedBonus = findBonusType(bonusTypes, formData.bonusType);

  const tdsSettingLabel = getTdsSettingLabel(selectedBonus?.taxDeductionBehaviour);

  const payoutMonthOptions = useMemo(() => getPayoutMonthOptions(), []);

  const payrollMonthStatus = usePayrollMonthStatus(
    formData.userOrgId ?? '',
    formData.payoutMonth ?? '',
  );

  const handleBonusTypeChange = (value: string) => {
    setFieldValue('bonusType', value);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (payrollMonthStatus.isBlocked) {
      return;
    }
    if (validate(bonusDetailsValidationSchema)) {
      updateStepStatus(bonusSteps.step1, 'success');
      setCurrentStep(bonusSteps.step2);
    }
  };

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <StepContentWrapper noBgIllustration width="462px">
        <Box marginBottom="spacing.8" display="flex" flexDirection="column" gap="spacing.2">
          <Heading weight="semibold" color="surface.text.gray.normal" size="large">
            {isEdit
              ? `Edit bonus for ${formData.employeeName ?? ''}`
              : `Allocate a bonus to ${formData.employeeName ?? ''}`}
          </Heading>
          <Text size="medium" weight="regular" variant="body" color="surface.text.gray.muted">
            Add a one-time payment from the component library. This amount is added over and above
            the current CTC
          </Text>
        </Box>

        <Box display="flex" flexDirection="column" gap="spacing.8">
          {isEdit && (
            <Alert
              description="The bonus type is fixed once allocated — its tax treatment defines the payout. To change it, cancel this bonus and allocate a new one. Everything else can be edited."
              color="information"
              isDismissible={false}
            />
          )}
          <Dropdown selectionType="single">
            <SelectInput
              label="Choose Bonus"
              name="bonusType"
              placeholder={isLoading ? 'Loading bonus library…' : 'Select from library'}
              value={formData.bonusType ?? ''}
              onChange={({ values }) => handleBonusTypeChange(values[0])}
              isDisabled={isLoading || isError || isEdit}
              isRequired
            />
            <DropdownOverlay>
              <ActionList>
                {bonusTypes.map((bonus) => (
                  <ActionListItem key={bonus.value} title={bonus.title} value={bonus.value} />
                ))}
              </ActionList>
            </DropdownOverlay>
          </Dropdown>

          {isError && (
            <Alert
              description="Could not load the bonus library. Please try again."
              color="negative"
              isDismissible={false}
              actions={{ primary: { text: 'Retry', onClick: () => refetch() } }}
            />
          )}

          {!isLoading && !isError && bonusTypes.length === 0 && (
            <Alert
              description="No bonus types are enabled for your organization. Add one in the component library first."
              color="notice"
              isDismissible={false}
            />
          )}

          {tdsSettingLabel && (
            <Alert
              description={`TDS Setting: ${tdsSettingLabel} (Configured in Library)`}
              color="information"
              isDismissible={false}
            />
          )}

          <TextInput
            label="Bonus Amount"
            name="bonusAmount"
            placeholder="Enter Bonus Amount"
            prefix="₹"
            type="number"
            value={formatBonusAmountInput(formData.bonusAmount ?? '')}
            onChange={(e) => {
              const next = parseBonusAmountInput(e.value ?? '');
              if (next !== null) {
                setFieldValue('bonusAmount', next);
              }
            }}
            isRequired
          />

          <Dropdown selectionType="single">
            <SelectInput
              label="Payment Status"
              name="paymentStatus"
              placeholder="Select Bonus Payment Status"
              value={formData.paymentStatus ?? ''}
              onChange={({ values }) => setFieldValue('paymentStatus', values[0])}
              isRequired
            />
            <DropdownOverlay>
              <ActionList>
                {PAYMENT_STATUS_OPTIONS.map((option) => (
                  <ActionListItem key={option.value} title={option.title} value={option.value} />
                ))}
              </ActionList>
            </DropdownOverlay>
          </Dropdown>

          <Box>
            {/* A month picker, not a date picker: `payroll_month` is yyyy-mm, so a
                day would be collected and then silently discarded — and under a
                custom payroll cycle a mid-month date can resolve to a different
                month than the one submitted. See BonusFormData.payoutMonth. */}
            <Dropdown selectionType="single">
              <SelectInput
                label="Payout Month"
                name="payoutMonth"
                placeholder="Select Payout Month"
                value={formData.payoutMonth}
                onChange={({ values }) => setFieldValue('payoutMonth', values[0])}
                isRequired
              />
              <DropdownOverlay>
                <ActionList>
                  {payoutMonthOptions.map((option) => (
                    <ActionListItem key={option.value} title={option.title} value={option.value} />
                  ))}
                </ActionList>
              </DropdownOverlay>
            </Dropdown>
            <Text
              variant="caption"
              size="small"
              color={
                payrollMonthStatus.error
                  ? 'feedback.text.negative.intense'
                  : 'surface.text.gray.muted'
              }
              marginTop="spacing.2">
              {payrollMonthStatus.error ??
                PAYOUT_MONTH_HELP_TEXT[formData.paymentStatus ?? ''] ??
                PAYOUT_MONTH_HELP_TEXT.to_be_paid}
            </Text>
          </Box>

          <TextArea
            label="Remark"
            labelPosition="top"
            placeholder="Other Details"
            {...register('remark')}
          />
        </Box>
      </StepContentWrapper>
      <StepActionsWrapper width="462px">
        <Button variant="tertiary" onClick={closeWizard}>
          Close
        </Button>
        <Button
          type="submit"
          isDisabled={payrollMonthStatus.isBlocked}
          isLoading={payrollMonthStatus.isChecking}>
          Next
        </Button>
      </StepActionsWrapper>
    </FormWrapper>
  );
};

export default BonusDetailsStep;
